import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Users, 
  Plus, 
  Trophy, 
  Copy, 
  LogIn,
  Crown,
  School,
  Settings,
  Loader2,
  UserPlus,
  ExternalLink,
  Clock,
  Calendar,
  RefreshCw,
  CheckCircle2,
  Timer,
  Star,
  Swords
} from 'lucide-react';
import SystemLeaguesDashboard from '../components/SystemLeaguesDashboard';
import MiniLeaguesDashboard from '../components/MiniLeaguesDashboard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LeaguesPage = () => {
  const { user, token } = useAuth();
  const [myLeagues, setMyLeagues] = useState([]);
  const [publicLeagues, setPublicLeagues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [selectedLeague, setSelectedLeague] = useState(null);

  useEffect(() => {
    if (token) {
      fetchMyLeagues();
      fetchPublicLeagues();
    }
  }, [token]);

  const fetchMyLeagues = async () => {
    try {
      const res = await fetch(`${API_URL}/api/leagues/my-leagues`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setMyLeagues(data.leagues || []);
    } catch (err) {
      console.error('Error fetching leagues:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicLeagues = async () => {
    try {
      const res = await fetch(`${API_URL}/api/leagues/public`);
      const data = await res.json();
      setPublicLeagues(data.leagues || []);
    } catch (err) {
      console.error('Error fetching public leagues:', err);
    }
  };

  const copyInviteCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Invite code copied!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 py-8 px-4" data-testid="leagues-page">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-white flex items-center gap-3">
              <Users className="h-8 w-8 text-emerald-400" />
              Leagues
            </h1>
            <p className="text-slate-400 mt-1">Create or join leagues to compete with friends</p>
          </div>
          <div className="flex gap-3">
            <Button 
              variant="outline" 
              className="border-slate-700 text-slate-300 hover:bg-slate-800"
              onClick={() => setShowJoinModal(true)}
              data-testid="join-league-btn"
            >
              <LogIn className="h-4 w-4 mr-2" />
              Join League
            </Button>
            <Button 
              className="bg-emerald-600 hover:bg-emerald-500"
              onClick={() => setShowCreateModal(true)}
              data-testid="create-league-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create League
            </Button>
          </div>
        </div>

        <Tabs defaultValue="system-leagues" className="space-y-6">
          <TabsList className="bg-slate-800 border-slate-700">
            <TabsTrigger value="system-leagues" className="flex items-center gap-2">
              <Trophy className="h-4 w-4" />
              System Leagues
            </TabsTrigger>
            <TabsTrigger value="mini-leagues" className="flex items-center gap-2">
              <Swords className="h-4 w-4" />
              Mini Leagues
            </TabsTrigger>
            <TabsTrigger value="my-leagues">My Leagues</TabsTrigger>
            <TabsTrigger value="public">Public Leagues</TabsTrigger>
          </TabsList>

          {/* System Leagues Tab */}
          <TabsContent value="system-leagues">
            <SystemLeaguesDashboard />
          </TabsContent>

          {/* Mini Leagues Tab */}
          <TabsContent value="mini-leagues">
            <MiniLeaguesDashboard />
          </TabsContent>

          {/* My Leagues Tab */}
          <TabsContent value="my-leagues">
            {myLeagues.length === 0 ? (
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No leagues yet</h3>
                  <p className="text-slate-400 mb-6">Create your own league or join one with an invite code</p>
                  <div className="flex gap-3 justify-center">
                    <Button 
                      variant="outline"
                      className="border-slate-700"
                      onClick={() => setShowJoinModal(true)}
                    >
                      <LogIn className="h-4 w-4 mr-2" />
                      Join League
                    </Button>
                    <Button 
                      className="bg-emerald-600 hover:bg-emerald-500"
                      onClick={() => setShowCreateModal(true)}
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create League
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {myLeagues.map((league) => (
                  <LeagueCard 
                    key={league.id} 
                    league={league} 
                    onCopyCode={copyInviteCode}
                    onSelect={() => setSelectedLeague(league)}
                    token={token}
                    onRefresh={fetchMyLeagues}
                  />
                ))}
              </div>
            )}
          </TabsContent>

          {/* Public Leagues Tab */}
          <TabsContent value="public">
            {publicLeagues.length === 0 ? (
              <Card className="bg-slate-900/50 border-slate-800">
                <CardContent className="py-12 text-center">
                  <Users className="h-12 w-12 text-slate-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">No public leagues</h3>
                  <p className="text-slate-400">There are no public leagues at the moment</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4 md:grid-cols-2">
                {publicLeagues.map((league) => (
                  <PublicLeagueCard 
                    key={league.id} 
                    league={league}
                    token={token}
                    onJoin={fetchMyLeagues}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create League Modal */}
      <CreateLeagueModal 
        open={showCreateModal} 
        onClose={() => setShowCreateModal(false)}
        token={token}
        onCreated={() => {
          fetchMyLeagues();
          setShowCreateModal(false);
        }}
      />

      {/* Join League Modal */}
      <JoinLeagueModal 
        open={showJoinModal} 
        onClose={() => setShowJoinModal(false)}
        token={token}
        onJoined={() => {
          fetchMyLeagues();
          setShowJoinModal(false);
        }}
      />

      {/* League Detail Modal */}
      {selectedLeague && (
        <LeagueDetailModal
          league={selectedLeague}
          open={!!selectedLeague}
          onClose={() => setSelectedLeague(null)}
          token={token}
        />
      )}
    </div>
  );
};

// League Card Component
const LeagueCard = ({ league, onCopyCode, onSelect, token, onRefresh }) => {
  const isOwner = league.user_role === 'owner';
  const isAdmin = league.user_role === 'admin';
  const [creatingNewSeason, setCreatingNewSeason] = useState(false);
  
  const getStatusBadge = (status) => {
    switch (status) {
      case 'upcoming':
        return <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30"><Clock className="h-3 w-3 mr-1" />Upcoming</Badge>;
      case 'active':
        return <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30"><Timer className="h-3 w-3 mr-1" />Active</Badge>;
      case 'completed':
        return <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30"><CheckCircle2 className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'archived':
        return <Badge className="bg-slate-500/20 text-slate-400 border-slate-500/30">Archived</Badge>;
      default:
        return null;
    }
  };

  const formatTimeRemaining = (timeRemaining) => {
    if (!timeRemaining) return null;
    const { days, hours } = timeRemaining;
    if (days > 7) return `${Math.floor(days / 7)} weeks left`;
    if (days > 0) return `${days}d ${hours}h left`;
    return `${hours}h left`;
  };

  const handleCreateNewSeason = async () => {
    setCreatingNewSeason(true);
    try {
      const res = await fetch(`${API_URL}/api/leagues/${league.id}/new-season`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || 'New season created!');
        if (onRefresh) onRefresh();
      } else {
        toast.error(data.detail || 'Failed to create new season');
      }
    } catch (err) {
      toast.error('Failed to create new season');
    } finally {
      setCreatingNewSeason(false);
    }
  };

  // Sponsor branding colors
  const sponsor = league.sponsor;
  const primaryColor = sponsor?.primary_color || '#8b5cf6';
  const isSponsored = league.is_sponsored && sponsor;
  
  return (
    <Card 
      className={`bg-slate-900/50 border-slate-800 hover:border-slate-700 transition-colors ${
        isSponsored ? 'border-l-4' : ''
      }`}
      style={isSponsored ? { borderLeftColor: primaryColor } : {}}
    >
      {/* Sponsor Banner */}
      {isSponsored && sponsor.banner_url && (
        <div className="h-24 overflow-hidden rounded-t-lg">
          <img 
            src={sponsor.banner_url} 
            alt={sponsor.sponsor_name}
            className="w-full h-full object-cover"
          />
        </div>
      )}

      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            {isSponsored && sponsor.logo_url ? (
              <img src={sponsor.logo_url} alt={sponsor.sponsor_name} className="h-6 w-6 rounded" />
            ) : league.league_type === 'edu' ? (
              <School className="h-5 w-5 text-blue-400" />
            ) : (
              <Trophy className="h-5 w-5 text-yellow-400" />
            )}
            <CardTitle className="text-white text-lg">
              {isSponsored && sponsor.sponsored_name ? sponsor.sponsored_name : league.name}
            </CardTitle>
          </div>
          <div className="flex gap-2 flex-wrap justify-end">
            {getStatusBadge(league.status)}
            {isOwner && (
              <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Owner</Badge>
            )}
            {isAdmin && !isOwner && (
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">Admin</Badge>
            )}
            {league.is_recurring && (
              <Badge className="bg-indigo-500/20 text-indigo-400 border-indigo-500/30">
                <RefreshCw className="h-3 w-3 mr-1" />S{league.season_number}
              </Badge>
            )}
          </div>
        </div>
        {isSponsored && (
          <p className="text-sm mt-1" style={{ color: primaryColor }}>
            Sponsored by {sponsor.sponsor_name}
          </p>
        )}
        {league.description && (
          <CardDescription className="text-slate-400 mt-1">{league.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-slate-400">Members</span>
          <span className="text-white font-medium">
            {league.member_count}
            {league.player_cap && ` / ${league.player_cap}`}
          </span>
        </div>
        
        {/* Duration Info */}
        {league.duration_weeks > 0 && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">Duration</span>
            <span className="text-white">{league.duration_weeks} weeks</span>
          </div>
        )}

        {/* Time Remaining */}
        {league.time_remaining && league.status === 'active' && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">Time Left</span>
            <span className="text-amber-400 font-medium flex items-center gap-1">
              <Timer className="h-3 w-3" />
              {formatTimeRemaining(league.time_remaining)}
            </span>
          </div>
        )}
        
        {league.school_name && (
          <div className="flex items-center justify-between text-sm">
            <span className="text-slate-400">School</span>
            <span className="text-white">{league.school_name}</span>
          </div>
        )}

        {/* Exclusive Cosmetics Indicator */}
        {isSponsored && sponsor.exclusive_cosmetics?.length > 0 && (
          <div className="flex items-center gap-2 text-sm">
            <Badge 
              className="bg-purple-500/20 text-purple-400"
              style={{ backgroundColor: `${primaryColor}20`, color: primaryColor }}
            >
              {sponsor.exclusive_cosmetics.length} Exclusive Rewards
            </Badge>
          </div>
        )}

        {league.invite_code && (isOwner || isAdmin) && (
          <div className="flex items-center justify-between">
            <span className="text-slate-400 text-sm">Invite Code</span>
            <div className="flex items-center gap-2">
              <code className="bg-slate-800 px-2 py-1 rounded text-emerald-400 font-mono">
                {league.invite_code}
              </code>
              <Button 
                size="sm" 
                variant="ghost" 
                className="h-8 w-8 p-0"
                onClick={() => onCopyCode(league.invite_code)}
              >
                <Copy className="h-4 w-4" />
              </Button>
            </div>
          </div>
        )}

        <div className="flex gap-2 pt-2">
          <Button 
            className="flex-1 bg-slate-800 hover:bg-slate-700 text-white"
            onClick={onSelect}
            style={isSponsored ? { borderColor: primaryColor, borderWidth: '1px' } : {}}
          >
            <Trophy className="h-4 w-4 mr-2" />
            Leaderboard
          </Button>
          {(isOwner || isAdmin) && (
            <Button 
              variant="ghost" 
              size="icon"
              className="text-slate-400 hover:text-white"
            >
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </div>

        {/* New Season Button for completed/recurring leagues */}
        {isOwner && (league.status === 'completed' || league.is_recurring) && (
          <Button 
            className="w-full bg-indigo-600 hover:bg-indigo-500"
            onClick={handleCreateNewSeason}
            disabled={creatingNewSeason}
          >
            {creatingNewSeason ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Create New Season
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

// Public League Card Component
const PublicLeagueCard = ({ league, token, onJoin }) => {
  const [joining, setJoining] = useState(false);

  const handleJoin = async () => {
    // For public leagues, we'd need to join without invite code
    // This would require a separate API endpoint
    toast.info('Use the invite code to join this league');
  };

  return (
    <Card className="bg-slate-900/50 border-slate-800">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <CardTitle className="text-white text-lg">{league.name}</CardTitle>
          <Badge variant="outline" className="border-slate-600 text-slate-400">
            {league.member_count}/{league.max_members}
          </Badge>
        </div>
        {league.description && (
          <CardDescription className="text-slate-400">{league.description}</CardDescription>
        )}
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between text-sm mb-4">
          <span className="text-slate-400">Created by</span>
          <span className="text-white">{league.owner_username}</span>
        </div>
        <Button 
          className="w-full bg-emerald-600 hover:bg-emerald-500"
          onClick={handleJoin}
          disabled={joining}
        >
          <UserPlus className="h-4 w-4 mr-2" />
          Join League
        </Button>
      </CardContent>
    </Card>
  );
};

// Create League Modal
const CreateLeagueModal = ({ open, onClose, token, onCreated }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [leagueType, setLeagueType] = useState('standard');
  const [schoolName, setSchoolName] = useState('');
  const [maxMembers, setMaxMembers] = useState(50);
  const [isPublic, setIsPublic] = useState(false);
  const [durationWeeks, setDurationWeeks] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [isRecurring, setIsRecurring] = useState(false);
  const [creating, setCreating] = useState(false);

  const durationOptions = [
    { weeks: 0, label: "No end date (continuous)" },
    { weeks: 6, label: "6 weeks" },
    { weeks: 13, label: "13 weeks (1 quarter)" },
    { weeks: 26, label: "26 weeks (6 months)" },
    { weeks: 52, label: "52 weeks (1 year)" }
  ];

  const handleCreate = async () => {
    if (!name.trim()) {
      toast.error('Please enter a league name');
      return;
    }

    setCreating(true);
    try {
      const body = {
        name: name.trim(),
        description: description.trim() || null,
        league_type: leagueType,
        school_name: leagueType === 'edu' ? schoolName.trim() : null,
        max_members: maxMembers,
        is_public: isPublic,
        duration_weeks: durationWeeks,
        is_recurring: isRecurring
      };

      // Add start date if specified
      if (startDate) {
        body.start_date = new Date(startDate).toISOString();
      }

      const res = await fetch(`${API_URL}/api/leagues/create`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(body)
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(`League "${data.name}" created! Invite code: ${data.invite_code}`);
        onCreated();
        // Reset form
        setName('');
        setDescription('');
        setLeagueType('standard');
        setSchoolName('');
        setMaxMembers(50);
        setIsPublic(false);
        setDurationWeeks(0);
        setStartDate('');
        setIsRecurring(false);
      } else {
        toast.error(data.detail || 'Failed to create league');
      }
    } catch (err) {
      toast.error('Failed to create league');
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-emerald-400" />
            Create League
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Create a new league and invite others to compete
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>League Name</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Finance 101 Class"
              className="bg-slate-800 border-slate-700"
              data-testid="league-name-input"
            />
          </div>

          <div className="space-y-2">
            <Label>Description (optional)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What's this league about?"
              className="bg-slate-800 border-slate-700"
            />
          </div>

          <div className="space-y-2">
            <Label>League Type</Label>
            <div className="flex gap-2">
              <Button
                type="button"
                variant={leagueType === 'standard' ? 'default' : 'outline'}
                className={leagueType === 'standard' ? 'bg-emerald-600' : 'border-slate-700'}
                onClick={() => setLeagueType('standard')}
              >
                <Trophy className="h-4 w-4 mr-2" />
                Standard
              </Button>
              <Button
                type="button"
                variant={leagueType === 'edu' ? 'default' : 'outline'}
                className={leagueType === 'edu' ? 'bg-blue-600' : 'border-slate-700'}
                onClick={() => setLeagueType('edu')}
              >
                <School className="h-4 w-4 mr-2" />
                Education
              </Button>
            </div>
          </div>

          {leagueType === 'edu' && (
            <div className="space-y-2">
              <Label>School/Institution Name</Label>
              <Input
                value={schoolName}
                onChange={(e) => setSchoolName(e.target.value)}
                placeholder="e.g., Lincoln High School"
                className="bg-slate-800 border-slate-700"
              />
            </div>
          )}

          {/* Duration Selection */}
          <div className="space-y-2">
            <Label>League Duration</Label>
            <select
              value={durationWeeks}
              onChange={(e) => setDurationWeeks(parseInt(e.target.value))}
              className="w-full bg-slate-800 border border-slate-700 rounded-md px-3 py-2 text-white"
              data-testid="duration-select"
            >
              {durationOptions.map(opt => (
                <option key={opt.weeks} value={opt.weeks}>{opt.label}</option>
              ))}
            </select>
            <p className="text-xs text-slate-500">
              {durationWeeks === 0 
                ? "League runs indefinitely until manually closed" 
                : `League will automatically complete after ${durationWeeks} weeks`}
            </p>
          </div>

          {/* Start Date */}
          <div className="space-y-2">
            <Label>Start Date (optional)</Label>
            <Input
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="bg-slate-800 border-slate-700"
              data-testid="start-date-input"
            />
            <p className="text-xs text-slate-500">
              Leave empty to start immediately
            </p>
          </div>

          <div className="space-y-2">
            <Label>Max Members</Label>
            <Input
              type="number"
              value={maxMembers}
              onChange={(e) => setMaxMembers(parseInt(e.target.value) || 50)}
              min={2}
              max={500}
              className="bg-slate-800 border-slate-700"
            />
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="isPublic"
                checked={isPublic}
                onChange={(e) => setIsPublic(e.target.checked)}
                className="rounded border-slate-700"
              />
              <Label htmlFor="isPublic" className="text-sm text-slate-300">
                Make league public (anyone can find and request to join)
              </Label>
            </div>

            {durationWeeks > 0 && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isRecurring"
                  checked={isRecurring}
                  onChange={(e) => setIsRecurring(e.target.checked)}
                  className="rounded border-slate-700"
                />
                <Label htmlFor="isRecurring" className="text-sm text-slate-300">
                  Recurring league (can create new seasons)
                </Label>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button 
            className="bg-emerald-600 hover:bg-emerald-500"
            onClick={handleCreate}
            disabled={creating}
            data-testid="create-league-submit"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
            Create League
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Join League Modal
const JoinLeagueModal = ({ open, onClose, token, onJoined }) => {
  const [inviteCode, setInviteCode] = useState('');
  const [joining, setJoining] = useState(false);

  const handleJoin = async () => {
    if (!inviteCode.trim()) {
      toast.error('Please enter an invite code');
      return;
    }

    setJoining(true);
    try {
      const res = await fetch(`${API_URL}/api/leagues/join`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ invite_code: inviteCode.trim().toUpperCase() })
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(data.message || `Joined ${data.league_name}!`);
        onJoined();
        setInviteCode('');
      } else {
        toast.error(data.detail || 'Failed to join league');
      }
    } catch (err) {
      toast.error('Failed to join league');
    } finally {
      setJoining(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <LogIn className="h-5 w-5 text-emerald-400" />
            Join League
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Enter the invite code shared by the league owner
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Invite Code</Label>
            <Input
              value={inviteCode}
              onChange={(e) => setInviteCode(e.target.value.toUpperCase())}
              placeholder="e.g., BFM9JK7G"
              className="bg-slate-800 border-slate-700 text-center text-xl font-mono tracking-widest"
              maxLength={10}
              data-testid="invite-code-input"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button 
            className="bg-emerald-600 hover:bg-emerald-500"
            onClick={handleJoin}
            disabled={joining}
            data-testid="join-league-submit"
          >
            {joining ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <LogIn className="h-4 w-4 mr-2" />}
            Join League
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// League Detail Modal with Leaderboard
const LeagueDetailModal = ({ league, open, onClose, token }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [leagueDetails, setLeagueDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (open && league) {
      fetchLeaderboard();
      fetchLeagueDetails();
    }
  }, [open, league]);

  const fetchLeaderboard = async () => {
    try {
      const res = await fetch(`${API_URL}/api/leagues/${league.id}/leaderboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLeaderboard(data.leaderboard || []);
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeagueDetails = async () => {
    try {
      const res = await fetch(`${API_URL}/api/leagues/${league.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      setLeagueDetails(data);
    } catch (err) {
      console.error('Error fetching league details:', err);
    }
  };

  const sponsor = leagueDetails?.sponsor;
  const primaryColor = sponsor?.primary_color || '#8b5cf6';

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-900 border-slate-800 text-white max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Sponsor Banner */}
        {leagueDetails?.is_sponsored && sponsor && (
          <div 
            className="rounded-lg p-4 mb-4 -mt-2"
            style={{ 
              background: `linear-gradient(135deg, ${primaryColor}20, ${sponsor.secondary_color || '#06b6d4'}10)`,
              borderLeft: `4px solid ${primaryColor}`
            }}
          >
            <div className="flex items-center gap-3">
              {sponsor.logo_url && (
                <img src={sponsor.logo_url} alt={sponsor.sponsor_name} className="h-8 w-8 rounded" />
              )}
              <div>
                <p className="text-sm text-slate-400">Sponsored by</p>
                <p className="font-semibold" style={{ color: primaryColor }}>
                  {sponsor.sponsor_name}
                </p>
              </div>
            </div>
            {sponsor.message && (
              <p className="text-sm text-slate-300 mt-2">{sponsor.message}</p>
            )}
            {sponsor.exclusive_cosmetics?.length > 0 && (
              <Badge className="mt-2 bg-purple-500/20 text-purple-400">
                {sponsor.exclusive_cosmetics.length} Exclusive Rewards
              </Badge>
            )}
          </div>
        )}

        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-yellow-400" />
            {leagueDetails?.sponsor?.sponsored_name || league.name} - Leaderboard
          </DialogTitle>
          {leagueDetails?.player_cap && (
            <p className="text-sm text-slate-400">
              {leagueDetails.member_count} / {leagueDetails.player_cap} players
              {leagueDetails.waitlist_count > 0 && ` • ${leagueDetails.waitlist_count} on waitlist`}
            </p>
          )}
        </DialogHeader>

        <div className="py-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
            </div>
          ) : leaderboard.length === 0 ? (
            <p className="text-center text-slate-400 py-8">No members yet</p>
          ) : (
            <div className="space-y-2">
              {leaderboard.map((entry, idx) => (
                <div 
                  key={entry.user_id}
                  className={`flex items-center justify-between p-3 rounded-lg ${
                    entry.is_you ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-slate-800/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                      entry.rank === 1 ? 'bg-yellow-500 text-black' :
                      entry.rank === 2 ? 'bg-slate-400 text-black' :
                      entry.rank === 3 ? 'bg-amber-600 text-black' :
                      'bg-slate-700 text-white'
                    }`}>
                      {entry.rank}
                    </span>
                    <div>
                      <p className="font-medium text-white">
                        {entry.username}
                        {entry.is_you && <span className="text-emerald-400 ml-2">(You)</span>}
                      </p>
                      {/* Show league-exclusive badge indicator */}
                      {entry.has_league_badge && leagueDetails?.is_sponsored && (
                        <Badge 
                          className="text-xs mt-1"
                          style={{ 
                            backgroundColor: `${primaryColor}20`,
                            color: primaryColor
                          }}
                        >
                          {sponsor?.sponsor_name} Champion
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-white">{entry.total_points} pts</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LeaguesPage;
